var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { marked } from "marked";
import { friendlyDocType } from "../shell/doc-labels";
let ChartViewer = class ChartViewer extends LitElement {
    constructor() {
        super(...arguments);
        this.document = null;
        this.zoomed = false;
        // Block the context menu (Save image / Open in new tab) across the chart + overlay.
        this.noContext = (e) => e.preventDefault();
        this.openZoom = () => {
            this.zoomed = true;
            document.addEventListener("keydown", this.onKey);
        };
        this.closeZoom = () => {
            this.zoomed = false;
            document.removeEventListener("keydown", this.onKey);
        };
        this.onKey = (e) => {
            if (e.key === "Escape")
                this.closeZoom();
        };
    }
    static { this.styles = css `
    :host, *, *::before, *::after { box-sizing: border-box; }
    :host { display: block; user-select: none; -webkit-user-select: none; }
    /* document header: clean title + category badge + timestamp/author */
    .meta { display: flex; align-items: center; gap: 9px; flex-wrap: wrap;
            padding-bottom: 12px; margin-bottom: 14px; border-bottom: 1px solid var(--color-slate-200); }
    .meta .title { font-family: var(--font-display); font-weight: 600; font-size: 15px;
                   color: var(--color-slate-900); }
    .meta .badge { font-size: 11px; font-weight: 600; color: var(--color-slate-600);
                   background: var(--color-slate-100); border: 1px solid var(--color-slate-200);
                   padding: 2px 8px; border-radius: 20px; }
    .meta .stamp { color: var(--color-slate-500); font-size: 12px; }
    /* uniform reading scale; record markdown headings tamed from browser-default.
       No max-width — the record fills the available chart pane to the edges. */
    .body { line-height: 1.6; font-size: var(--fs-doc); color: var(--color-slate-800); }
    .body h1, .body h2 { font-family: var(--font-display); font-size: var(--fs-doc-h); font-weight: 600;
                         color: var(--color-slate-900); margin: 18px 0 8px; }
    .body h3 { font-size: 14px; font-weight: 600; color: var(--color-slate-900); margin: 14px 0 6px; }
    .body p { margin: 0 0 11px; }
    .body strong, .body b { color: var(--color-slate-900); font-weight: 600; }
    img { max-width: 100%; cursor: zoom-in; -webkit-user-drag: none; user-select: none;
          -webkit-user-select: none; pointer-events: auto; }
    /* Full-screen viewer overlay. Fixed = viewport-relative even inside shadow DOM. */
    .overlay { position: fixed; inset: 0; z-index: 2000;
               background: rgba(15, 23, 42, 0.92);
               display: flex; align-items: center; justify-content: center;
               cursor: zoom-out; }
    .overlay img { max-width: 95vw; max-height: 95vh; cursor: zoom-out;
                   box-shadow: 0 10px 50px rgba(0, 0, 0, 0.6); border-radius: 4px; }
    .hint { position: fixed; bottom: 18px; left: 0; right: 0; text-align: center;
            color: rgba(255, 255, 255, 0.7); font-size: 13px; }
  `; }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener("keydown", this.onKey);
    }
    render() {
        if (!this.document)
            return html `<p>No document selected.</p>`;
        const d = this.document;
        return html `
      <div class="meta">
        <span class="title">${friendlyDocType(d.type)}</span>
        ${d.category ? html `<span class="badge">${d.category}</span>` : ""}
        <span class="stamp">${new Date(d.timestamp_iso).toLocaleString("en-AU")}${d.author ? ` · ${d.author}` : ""}</span>
      </div>
      <div class="body chart" @contextmenu=${this.noContext}>
        ${d.render === "typed"
            ? unsafeHTML(marked.parse(d.body_md ?? "", { async: false }))
            : html `<img src=${d.image_url} alt=${friendlyDocType(d.type)} draggable="false"
                      @contextmenu=${this.noContext} @click=${this.openZoom} />`}
      </div>
      ${this.zoomed && d.image_url
            ? html `<div class="overlay" @click=${this.closeZoom} @contextmenu=${this.noContext}>
              <img src=${d.image_url} alt=${d.type} draggable="false"
                   @contextmenu=${this.noContext} />
              <div class="hint">Click anywhere or press Esc to close</div>
            </div>`
            : ""}
    `;
    }
};
__decorate([
    property({ attribute: false })
], ChartViewer.prototype, "document", void 0);
__decorate([
    state()
], ChartViewer.prototype, "zoomed", void 0);
ChartViewer = __decorate([
    customElement("chart-viewer")
], ChartViewer);
export { ChartViewer };
